/**
 * Interactive state tokens for components
 * Centralizes hover, active, focus states and overlay backgrounds
 */

"use client";

import { useColorMode } from "@chakra-ui/react";
import { useContext } from "react";
import type { DashboardMode } from "@/components/dashboard/DashboardThemeProvider";
import { getDashboardTheme } from "@/components/dashboard/theme";
import { DashboardThemeContext } from "@/components/dashboard/DashboardThemeProvider";
import { usePathname } from "next/navigation";
import { isDashboardAppearanceRoute } from "@/lib/utils/theme-scope";
import type { DashboardAppearance } from "@/lib/theme/dashboard-appearance";

/**
 * Convert hex to rgba helper
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/**
 * Get interactive state tokens for a specific mode
 */
export const getInteractiveTokens = (
  mode: DashboardMode,
  paletteId?: string,
  customPrimaryColor?: string | null,
  appearance: DashboardAppearance = "modern",
) => {
  const isClassic = appearance === "classic";
  const theme = getDashboardTheme(
    mode,
    paletteId,
    customPrimaryColor,
    true,
    "medium",
    appearance,
  );
  const isDark = theme.mode === "dark";
  const primaryHex = theme.accent.primary;

  return {
    // Button hover/active states
    button: {
      primary: {
        hover: isDark ? hexToRgba(primaryHex, 0.85) : hexToRgba(primaryHex, 0.9),
        active: isDark ? hexToRgba(primaryHex, 0.75) : hexToRgba(primaryHex, 0.8),
      },
      muted: {
        hover: isDark ? hexToRgba(primaryHex, 0.15) : hexToRgba(primaryHex, 0.18),
        active: isDark ? hexToRgba(primaryHex, 0.2) : hexToRgba(primaryHex, 0.22),
      },
      ghost: {
        hover: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,13,16,0.08)",
      },
    },
    // IconButton hover/active states
    iconButton: {
      primary: {
        hover: isDark ? hexToRgba(primaryHex, 0.2) : hexToRgba(primaryHex, 0.18),
        active: isDark ? hexToRgba(primaryHex, 0.25) : hexToRgba(primaryHex, 0.22),
      },
      muted: {
        hover: isDark ? hexToRgba(primaryHex, 0.15) : hexToRgba(primaryHex, 0.18),
        active: isDark ? hexToRgba(primaryHex, 0.2) : hexToRgba(primaryHex, 0.22),
      },
      ghost: {
        hover: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,13,16,0.08)",
      },
    },
    // Overlay backgrounds
    overlay: {
      modal: isDark ? "rgba(0,0,0,0.8)" : "rgba(11,13,16,0.6)",
      drawer: isDark ? "rgba(0,0,0,0.8)" : "rgba(11,13,16,0.6)",
    },
    // Neutral backgrounds
    neutral: {
      bg: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,13,16,0.08)",
      bgStrong: isDark ? "rgba(255,255,255,0.1)" : "rgba(11,13,16,0.08)",
    },
    // Backdrop blur values (glassmorphism)
    backdrop: {
      blur: isClassic ? "none" : "blur(14px)",
      blurStrong: isClassic ? "none" : "blur(14px)",
      blurOverlay: isClassic ? "none" : "blur(8px)",
    },
  } as const;
};

/**
 * Hook to get interactive tokens
 * Must be used in client components only
 * Works both inside and outside DashboardThemeProvider
 */
export const useInteractiveTokens = () => {
  // Check if we're inside DashboardThemeProvider
  const dashboardContext = useContext(DashboardThemeContext);
  
  const pathname = usePathname();

  if (dashboardContext !== undefined) {
    const {
      mode,
      colorPalette,
      previewPalette,
      customColor,
      previewCustomColor,
      effectiveDashboardAppearance,
    } = dashboardContext;
    const activePalette = previewPalette || colorPalette;
    const activeCustomColor =
      previewCustomColor || (activePalette === "custom" ? customColor : null);
    const appearance = isDashboardAppearanceRoute(pathname)
      ? effectiveDashboardAppearance
      : "modern";
    return getInteractiveTokens(
      mode,
      activePalette,
      activeCustomColor,
      appearance,
    );
  }
  
  // Fallback: Use Chakra UI color mode
  const { colorMode } = useColorMode();
  const mode: DashboardMode = colorMode === "dark" ? "dark" : "light";
  return getInteractiveTokens(mode);
};

